import { type Dispatch, Fragment, type ReactElement, type SetStateAction } from "react"

import { useTranslation } from "react-i18next"

import SearchIcon from "@mui/icons-material/Search"
import { Grid } from "@mui/material"
import type { BarDatum } from "@nivo/bar"
import { ResponsiveBar } from "@nivo/bar"
import type { InputChangeEvent } from "@synapse-analytics/synapse-ui"
import { InputText, Pagination, Typography } from "@synapse-analytics/synapse-ui"
import { truncate } from "lodash"

import { getTheme } from "../../../hooks/UseTheme"
import { NivoTheme } from "../../../themes/nivoTheme"
import { ChartsContainer } from "../ChartsContainer"

import styles from "./FeatureImportanceChart.module.scss"

type FeatureImportanceProps = {
  chartData: BarDatum[]
  title: string
  isLoading: boolean
  graphHeight: number
  tooltipText?: string
  currentPage: number
  numberOfBarsPerPage: number
  setCurrentPage: Dispatch<SetStateAction<number>>
  searchHandler: (e: InputChangeEvent) => void
}

/**
 * A component to render a bar chart. It renders a search bar to search items in the chart,
 * a chart to display the data, and a pagination to navigate through the data.
 * @param {FeatureImportanceProps} props
 * @returns {ReactElement}
 */
export function FeatureImportanceChart(props: Readonly<FeatureImportanceProps>): ReactElement {
  const { t } = useTranslation("graphs")

  const {
    chartData,
    title,
    graphHeight,
    isLoading,
    tooltipText,
    currentPage,
    numberOfBarsPerPage,
    setCurrentPage,
    searchHandler,
  } = props

  const theme = getTheme()

  return (
    <ChartsContainer
      title={title}
      isLoading={isLoading}
      graphHeight={graphHeight + 64}
      tooltipText={tooltipText}
      gutterBottom
    >
      <div className={styles.chartContentContainer}>
        <div>
          <InputText
            handleChange={searchHandler}
            placeholder={t("featureImportanceChart.search.placeholder")}
            disabled={isLoading}
            variant="filled"
            width={300}
            hideDescription
            startAdornment={
              <SearchIcon
                fontSize="small"
                style={{
                  paddingTop: "2px",
                  fill: "var(--base-text-2)",
                }}
              />
            }
          />
        </div>
        {chartData.length === 0 ? (
          <Typography variant="h1-bold" className={styles.emptyView} align="center" textTransform="capitalize">
            {t("featureImportanceChart.emptyState.noDataMatchesSearch")}
          </Typography>
        ) : (
          <Fragment>
            <div style={{ height: `${graphHeight}px` }}>
              <ResponsiveBar
                data={chartData
                  .slice((currentPage - 1) * numberOfBarsPerPage, currentPage * numberOfBarsPerPage)
                  .reverse()}
                theme={NivoTheme(theme)}
                keys={["value"]}
                indexBy="label"
                margin={{ right: 50, left: 100 }}
                padding={0.3}
                layout="horizontal"
                axisBottom={null}
                axisRight={{
                  tickSize: 0,
                  tickPadding: 12,
                  tickRotation: 0,
                  format: (value) => {
                    const dataIndex = chartData.findIndex((d) => d.label === value)
                    return dataIndex !== -1 ? chartData[dataIndex]?.value : value
                  },
                }}
                axisLeft={{
                  tickSize: 0,
                  tickPadding: 12,
                  tickRotation: 0,
                  renderTick: ({ value, y, textX, textAnchor }) => {
                    return (
                      <g>
                        <title>{value}</title>
                        <text
                          dominant-baseline="central"
                          textAnchor={textAnchor as React.SVGAttributes<SVGTextElement>["textAnchor"]}
                          x={textX}
                          y={y}
                          style={{
                            fontFamily: "sans-serif",
                            fontSize: "11px",
                            fill: theme.palette.base.text[1],
                            outlineWidth: "0px",
                            outlineColor: "transparent",
                          }}
                        >
                          {truncate(value, { length: 15 })}
                        </text>
                      </g>
                    )
                  },
                }}
                enableGridX={false}
                enableGridY={false}
                enableLabel={false}
                colors={theme.palette.blue.background[1]}
                borderRadius={4}
                tooltip={(slice) => (
                  <div className={"chart-tooltip"}>
                    <Grid container direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
                      <Grid item>
                        <span
                          key="chip"
                          style={{ display: "block", width: "12px", height: "12px", background: slice.color }}
                        />
                      </Grid>
                      <Grid item>
                        <Typography variant="label-bold" variantColor={2}>
                          {slice.data.label} : {slice.data.value}
                        </Typography>
                      </Grid>
                    </Grid>
                  </div>
                )}
              />
            </div>
            <Pagination
              page={currentPage}
              onPageChange={(page: number): void => setCurrentPage(page)}
              pageSize={numberOfBarsPerPage}
              totalCount={chartData.length}
              hidePageSize
              variant={"complex"}
            />
          </Fragment>
        )}
      </div>
    </ChartsContainer>
  )
}
